import { useEffect, useMemo } from 'react';

import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import keyBy from 'lodash/keyBy';
import keys from 'lodash/keys';
import mapValues from 'lodash/mapValues';
import pickBy from 'lodash/pickBy';
import some from 'lodash/some';
import { useCustomCompareMemo } from 'use-custom-compare';

import type { FeatureConfigData, FeatureConfigInfo } from '@change-corgi/core/fcm';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type { FcmCacheState } from './context';
import { useFcmState } from './context';
import { reportMultipleConfigs } from './shared/reportMultipleConfigs';
import type { HookOptions, MultiFcmState } from './shared/types';
import { updateConfigsMap } from './shared/updateConfigsMap';

export function isCombinedStatesEqual(state1: FcmCacheState['cache'], state2: FcmCacheState['cache']): boolean {
	if (!isEqual(keys(state1), keys(state2))) {
		return false;
	}
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return keys(state1).every((key) => state1[key as never] === state2[key as never]);
}

// eslint-disable-next-line max-lines-per-function
export function createFcmHook<T extends Record<string, FeatureConfigInfo>, KEY extends string = 'values'>(
	configs: T,
	{ valueProperty }: HookOptions<KEY> = {},
): () => MultiFcmState<T, KEY> {
	const multipleConfigs = updateConfigsMap(configs);

	const names = keys(keyBy(configs, (config) => config.name));

	const getFromCache = (cache: FcmCacheState['cache']) => {
		return names.reduce<FcmCacheState['cache']>((acc, name) => {
			// eslint-disable-next-line no-param-reassign
			acc[name] = cache[name];
			return acc;
		}, {});
	};

	return (): MultiFcmState<T, KEY> => {
		const [state, { addToQueue }] = useFcmState();

		const utilityContext = useUtilityContext();

		useEffect(() => {
			reportMultipleConfigs(multipleConfigs, utilityContext);
		}, [utilityContext]);

		const fcmStatesSel = getFromCache(state.cache);

		const fcmStates = useCustomCompareMemo(
			() => fcmStatesSel,
			[fcmStatesSel],
			([c1], [c2]) => isCombinedStatesEqual(c1, c2),
		);

		// fcmStates = { fcm_labels: { status: 'loaded' }, fcm_enabled: undefined }

		const undefinedFcmStates = useMemo(() => pickBy(fcmStates, (fcmState) => !fcmState), [fcmStates]);

		// undefinedFcmStates = { fcm_enabled: undefined }

		const fcmToLoad = useMemo(
			() =>
				keys(undefinedFcmStates).reduce<FeatureConfigInfo[]>((acc, name) => {
					// eslint-disable-next-line lodash/matches-prop-shorthand, lodash/matches-shorthand
					const config = find(configs, (c) => c.name === name);
					if (config) acc.push(config);
					return acc;
				}, []),
			[undefinedFcmStates],
		);

		// fcmToLoad = [{ name: 'fcm_enabled', ... }]

		useEffect(() => {
			if (!Object.keys(fcmToLoad).length) return;
			addToQueue(fcmToLoad);
		}, [addToQueue, fcmToLoad]);

		// so the loading object is always the same for a specific config
		// eslint-disable-next-line react-hooks/exhaustive-deps
		const loadingState: MultiFcmState<T, KEY> = useMemo(() => ({ status: 'loading' as const }), [configs]);

		return useMemo(() => {
			const anyLoading = !!some(fcmStates, (fcmState) => !fcmState || fcmState.status === 'loading');
			if (anyLoading) {
				return loadingState;
			}

			/* eslint-disable @typescript-eslint/consistent-type-assertions */
			return {
				status: 'loaded' as const,
				[valueProperty || 'values']: mapValues(configs, ({ name }) => {
					if (!fcmStates[name]) {
						return undefined;
					}
					return (fcmStates[name] as { status: 'loaded'; value: T }).value;
				}) as FeatureConfigData<T>,
			} as MultiFcmState<T, KEY>;
			/* eslint-enable @typescript-eslint/consistent-type-assertions */
		}, [loadingState, fcmStates]);
	};
}
